/**
 * WizardProgressRail — the always-visible 7-step indicator that sits at the
 * top of every wizard screen.
 *
 * Phase 1 of the Feb 2026 redesign (LoopAI_Full_Flow_Spec.docx §3 + §10):
 * users were abandoning mid-flow because the existing UI had no progress
 * indicator. This rail fixes the single highest-impact problem first.
 *
 * Mapping for the Feb 2026 redesign (LoopAI_Full_Flow_Spec.docx §3 + §10):
 *   App step name → Rail step
 *     'project'    → Step 1 · Goal
 *     'script'     → Step 2 · Story
 *     'concept'    → Steps 3 + 4 · Concept + Script (script is generated as a
 *                    side-effect of picking a concept, so they're collapsed)
 *     'characters' → Step 5 · Cast
 *     'branding'   → Step 6 · Branding
 *     'tier'       → Step 7 · Quality
 *
 * Auto-completion: when the user takes the "I already have a script" or
 * "I'll record narration" path, they jump straight from `'script'` →
 * `'characters'`, skipping `'concept'`. The rail still marks Concept + Script
 * as complete because APP_STEP_ORDER puts `'concept'` between `'script'`
 * and `'characters'` — any step with a lower order than the current one
 * is rendered as complete.
 *
 * Props
 * ─────
 *   currentAppStep — one of 'project' | 'script' | 'characters' | 'branding' | 'tier'.
 *                    Anything else hides the rail (storyboard/studio/dashboard).
 *   onJumpTo(appStep) — optional. If provided, completed steps become
 *                       clickable so the user can go back. Pass `null` to
 *                       disable jump-back navigation.
 */
import React from 'react';
import { Check } from 'lucide-react';

const STEPS = [
    { n: 1, label: 'Goal',     appStep: 'project'    },
    { n: 2, label: 'Story',    appStep: 'script'     },
    { n: 3, label: 'Concept',  appStep: 'concept'    },
    { n: 4, label: 'Script',   appStep: 'concept'    },
    { n: 5, label: 'Cast',     appStep: 'characters' },
    { n: 6, label: 'Branding', appStep: 'branding'   },
    { n: 7, label: 'Quality',  appStep: 'tier'       },
];

const APP_STEP_ORDER = ['project', 'script', 'concept', 'characters', 'branding', 'tier'];

export const WizardProgressRail = ({ currentAppStep, onJumpTo = null, subtitle = '7 quick steps · about 5 minutes' }) => {
    if (!APP_STEP_ORDER.includes(currentAppStep)) return null;
    const currentIdx = APP_STEP_ORDER.indexOf(currentAppStep);

    // "Active" steps: any rail step whose appStep matches the current one.
    // (During Phase 1, script-stage shows steps 2+3+4 all active.)
    const isActive   = (s) => s.appStep === currentAppStep;
    const isComplete = (s) => APP_STEP_ORDER.indexOf(s.appStep) < currentIdx;

    // Headline label — uses the FIRST active step's number for the "Step X of 7" copy.
    const firstActive = STEPS.find(isActive);
    const headline    = firstActive ? `Step ${firstActive.n} of 7 · ${firstActive.label}` : '';

    return (
        <div className="w-full max-w-6xl mx-auto px-4 pt-4 pb-2" data-testid="wizard-progress-rail">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-violet-300" data-testid="wizard-progress-headline">
                    {headline}
                </h2>
                <p className="text-[10px] text-zinc-500" data-testid="wizard-progress-subtitle">{subtitle}</p>
            </div>
            <ol className="flex items-stretch gap-1.5 w-full">
                {STEPS.map((s) => {
                    const active   = isActive(s);
                    const complete = isComplete(s);
                    const muted    = !active && !complete;
                    const clickable = !!onJumpTo && complete;
                    const base = 'flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-[11px] font-medium';
                    const style = active
                        ? 'bg-violet-500/15 border-violet-500/50 text-white ring-1 ring-violet-500/30'
                        : complete
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/15'
                            : 'bg-white/5 border-white/10 text-zinc-500';
                    return (
                        <li key={s.n} className="flex-1">
                            <button
                                type="button"
                                onClick={clickable ? () => onJumpTo(s.appStep) : undefined}
                                disabled={!clickable}
                                className={`${base} ${style} ${clickable ? 'cursor-pointer' : 'cursor-default'} w-full`}
                                data-testid={`wizard-step-${s.n}`}
                                aria-current={active ? 'step' : undefined}
                            >
                                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 ${
                                    active   ? 'bg-violet-500 text-white' :
                                    complete ? 'bg-emerald-500 text-white' :
                                                'bg-white/10 text-zinc-500'
                                }`}>
                                    {complete ? <Check className="w-3 h-3" /> : s.n}
                                </span>
                                <span className={`truncate ${muted ? 'text-zinc-500' : ''}`}>{s.label}</span>
                            </button>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

export default WizardProgressRail;
